import {gql} from '@apollo/client';
import * as log from '../util/log';
import {NudgeCache} from './cache/init';
import {GET_MESSAGE} from './query/GetMessage.gql';

/**
 * Global singleton server-sent events stream.
 */
let _stream: EventSource | null = null;

/**
 * Events that represent an incoming message of some type.
 */
type IncomingMessageEvent = 'msg.send' | 'msg.receive';

/**
 * Handle the receipt of a new message.
 *
 * These messages can either come from send or receive events.
 *
 * @param stream - The EventSource stream to listen on.
 * @param eventType - The type of incoming message event ('msg.send' or 'msg.receive').
 * @param cache - The NudgeCache instance to write the message to.
 * @returns A function to remove the event listener.
 */
const handleNewMessage = (
  stream: EventSource,
  eventType: IncomingMessageEvent,
  cache: NudgeCache,
) => {
  const eventHandler = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      log.debug(`Received ${eventType} event ${data.id}`);
      cache.writeQuery({
        query: GET_MESSAGE,
        data: {
          node: data,
        },
        variables: {id: data.id},
      });
    } catch (error) {
      log.error(`Failed to parse ${eventType} event data:`, error as Error);
    }
  };

  stream.addEventListener(eventType, eventHandler);

  return () => {
    log.debug(`Removing event listener for ${eventType}`);
    stream.removeEventListener(eventType, eventHandler);
  };
};

/**
 * Initialize the event stream singleton.
 */
export const initEventStream = (source: string, cache: NudgeCache) => {
  if (!_stream || _stream.readyState === EventSource.CLOSED) {
    _stream = _newEventStream(source, cache);
  } else {
    log.debug('SSE stream already exists, not creating a new one');
  }

  // Function to clean up the stream when no longer needed.
  const cleanup = () => {
    log.debug('Cleaning up SSE stream');
    if (_stream) {
      _stream.close();
      log.debug('SSE stream closed');
      _stream = null;
    } else {
      log.debug('No SSE stream to close');
    }
  };
  return cleanup;
};

/**
 * Create a new server-sent events stream.
 */
export const _newEventStream = (
  source: string,
  cache: NudgeCache,
  retries: number = 0,
) => {
  log.debug(`Creating new SSE stream for source: ${source}`);
  const newStream = new EventSource(source);

  newStream.onopen = () => {
    log.debug('SSE connection opened');
  };

  newStream.onerror = () => {
    // Exponential backoff with a cap at 30 seconds
    const delay = Math.min(2 ** retries, 30);
    if (_stream) {
      _stream.close();
      _stream = null;
    }
    log.debug(`Error in SSE stream, reconnecting in ${delay}s ...`);
    setTimeout(() => {
      _stream = _newEventStream(source, cache, retries + 1);
    }, delay * 1000);
  };

  newStream.addEventListener('connected', () => {
    log.debug('SSE connection established');
  });

  newStream.addEventListener('<3', (event) => {
    log.debug('SSE heartbeat ' + event.data);
  });

  // Handle REVIEW QUEUE events
  newStream.addEventListener('rq.entry', (event) => {
    log.debug('Received rq.entry' + event.data);
  });

  // Handle STATUS events
  newStream.addEventListener('msg.status', (event) => {
    try {
      const data = JSON.parse(event.data);
      log.debug(`Received msg.status event ${data.id}: ${data.status}`);
      cache.writeQuery({
        query: gql`
          query getMessageStatus($id: ID!) {
            node(id: $id) {
              ... on Message {
                id
                status
              }
            }
          }
        `,
        data: {
          node: {
            __typename: 'Message',
            id: data.id,
            status: data.status,
          },
        },
        variables: {id: data.id},
      });
    } catch (error) {
      log.error('Failed to parse msg.status event data:', error as Error);
    }
  });

  // Handle SEND events
  handleNewMessage(newStream, 'msg.send', cache);

  // Handle RECEIVE events
  handleNewMessage(newStream, 'msg.receive', cache);

  return newStream;
};
